import React from 'react';
import { User as UserType } from '../../services/userManagementService';
import { useAppSelector } from '../../store/hooks';
import UserCard from './UserCard';

interface UserGridViewProps {
  users: UserType[];
  onEdit: (user: UserType) => void;
  onToggleStatus: (user: UserType) => void;
  onDelete: (user: UserType) => void;
  onShowDetails?: (user: UserType) => void;
  loading?: boolean;
  getRoleColor: (role: string) => string;
}

const UserGridView: React.FC<UserGridViewProps> = ({
  users,
  onEdit,
  onToggleStatus,
  onDelete,
  onShowDetails,
  loading = false,
  getRoleColor
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {[...Array(8)].map((_, i) => (
          <div key={i} className={`rounded-lg shadow p-4 sm:p-6 animate-pulse ${
            theme === 'dark' 
              ? 'bg-gray-900/80 border border-gray-700/50' 
              : 'bg-white border border-gray-200'
          }`}>
            <div className="flex items-center space-x-3 sm:space-x-4 mb-3 sm:mb-4">
              <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-full ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
              }`}></div>
              <div className="space-y-2 flex-1">
                <div className={`h-3 sm:h-4 rounded w-3/4 ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`}></div>
                <div className={`h-2 sm:h-3 rounded w-1/2 ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`}></div>
              </div>
            </div>
            <div className="space-y-2">
              <div className={`h-2 sm:h-3 rounded ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
              }`}></div>
              <div className={`h-2 sm:h-3 rounded w-2/3 ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
              }`}></div>
              <div className={`h-2 sm:h-3 rounded w-1/2 ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
              }`}></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-8 sm:py-12">
        <div className={`mx-auto h-10 w-10 sm:h-12 sm:w-12 mb-3 sm:mb-4 ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
        }`}>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
        <h3 className={`text-base sm:text-lg font-medium mb-2 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>No users found</h3>
        <p className={`text-sm sm:text-base ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>
          Get started by creating a new user.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 items-stretch">
      {users.map((user) => (
        <UserCard
          key={user.id}
          user={user}
          onEdit={onEdit}
          onToggleStatus={onToggleStatus}
          onDelete={onDelete}
          onShowDetails={onShowDetails}
          getRoleColor={getRoleColor}
        />
      ))}
    </div>
  );
};

export default UserGridView;
